import React, { useCallback, useEffect, useRef, useState } from "react";
import type { MutableRefObject } from "react";
import ReactDOM from "react-dom";
import { useFloatingPosition } from "../utils/useFloatingPosition";
import type { FloatingPlacement } from "../utils/floatingUtils";

export type PopoverControls = {
  isOpen: boolean;
  triggerRef: MutableRefObject<HTMLElement | null>;
  closePopover: () => void;
  togglePopover: () => void;
};

export type PopoverContentControls = PopoverControls & {
  contentRef: MutableRefObject<HTMLDivElement | null>;
};

type PopoverProps = {
  children: (controls: PopoverControls) => React.ReactNode;
  content: React.ReactNode | ((controls: PopoverContentControls) => React.ReactNode);
  defaultOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  placement?: FloatingPlacement;
  offset?: number;
  viewportPadding?: number;
  contentClassName?: string;
  contentStyle?: React.CSSProperties;
  closeOnEscape?: boolean;
  ignoreClickOutsideSelector?: string;
  "data-testid"?: string;
};

export const Popover: React.FC<PopoverProps> = ({
  children,
  content,
  defaultOpen = false,
  onOpenChange,
  placement = "bottom-right",
  offset = 8,
  viewportPadding = 16,
  contentClassName,
  contentStyle,
  closeOnEscape = true,
  ignoreClickOutsideSelector,
  "data-testid": testId,
}) => {
  const triggerRef = useRef<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const reportedOpen = useRef(defaultOpen);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const closePopover = useCallback(() => setIsOpen(false), []);
  const togglePopover = useCallback(() => setIsOpen((currentOpen) => !currentOpen), []);

  useEffect(() => {
    if (reportedOpen.current === isOpen) return;
    reportedOpen.current = isOpen;
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  const { floatingStyle, currentPlacement } = useFloatingPosition({
    active: isOpen,
    anchorRef: triggerRef,
    floatingRef: contentRef,
    placement,
    offset,
    viewportPadding,
  });

  useEffect(() => {
    if (!isOpen) return;
    const isInside = (target: EventTarget | null) => {
      if (!(target instanceof Node)) return true;
      if (contentRef.current?.contains(target) || triggerRef.current?.contains(target)) return true;
      return Boolean(ignoreClickOutsideSelector && target instanceof Element && target.closest(ignoreClickOutsideSelector));
    };

    let pressedInside = false;
    const handlePointerDown = (event: PointerEvent) => {
      pressedInside = isInside(event.target);
    };
    const handleClick = (event: MouseEvent) => {
      if (!pressedInside && !isInside(event.target)) closePopover();
      pressedInside = false;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (closeOnEscape && event.key === "Escape") closePopover();
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("click", handleClick, true);
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [closeOnEscape, closePopover, ignoreClickOutsideSelector, isOpen]);

  const controls: PopoverControls = {
    isOpen,
    triggerRef,
    closePopover,
    togglePopover,
  };

  return (
    <>
      {children(controls)}
      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={contentRef}
            className={contentClassName}
            style={{ ...floatingStyle, zIndex: 10001, ...contentStyle }}
            data-placement={currentPlacement}
            data-testid={testId}
          >
            {typeof content === "function"
              ? content({
                  ...controls,
                  contentRef,
                })
              : content}
          </div>,
          document.body
        )}
    </>
  );
};
